// The requisition screens' words (lane B2 S09): the requisition header, its tabs and right rail, the Coordinator's
// create/post card and the posting strategy, the Assignment card and the Compensation card. Every entry quotes his
// glossary (the D0 label suites discover this module, a lane folder of src/views, and hold it to the same rules as
// src/mc). Words the design system already carries (status chains, roles, verbs, Reason, Requisition timeline,
// Calendar Time …) are imported from it where they are drawn, never redefined here. The DS calls the first tab
// *Overview* and the first rail card *Properties* (DC-65, a design column, N-1): neither is his, so the tab carries his
// name for the record, *Requisition*, and the rail card has no title of its own (its rows are his field names).

import { defineLabels } from "@/mc/labels";

const REQ = "| Requisition / req | \"Coordinator creates and posts\"; \"REQ ID\" (Email card field).";
const PRINCIPLES = "\"Action-driven, not navigation-driven: show current state, next action, blockers, risks, recommendations, and recent changes.\"";
const POSTING = "| Posting strategy | \"Preserve proposed vs current posting strategy and reason for change.\" \"Original posting type + current posting strategy.\"";
const FIELDS =
  "\"title, HM, new/replacement, replacement employee, location, openings, people-manager/direct reports, posting type, notes, department, job family, area of interest, job code, org alignment, headcount approval, cost center, justification, final posting validation.\"";
const INTAKE_PANEL = "| Intake & Panel scheduling screen | V1 locator: \"Intake & Panel scheduling screen, Mission Control Coordinator Workbench…\" | Intake & Panel (nav) (same)";
const VERBS =
  "his verbs (glossary §17): Certify · Approve · Return · Assign · Post · Propose slots · Schedule · Reschedule · Cancel · Retry · Review · Release";
const PHASE_FILTER = "| Phase | a filter (\"status, phase, health\");";
const DAYS_OPEN = "| Days open | report \"Days open, active days, hold days, health\"; Email card field \"Days Open\".";

/** The requisition's header, tabs and rail. */
export const REQ_LABELS = /* @__PURE__ */ defineLabels({
  requisition: { text: "Requisition" },
  reqId: { text: "REQ ID" },
  intakePanel: { text: "Intake & Panel" },
  nextAction: { text: "Next action" },
  title: { text: "Title" },
  location: { text: "Location" },
  department: { text: "Department" },
  openings: { text: "Openings" },
  daysOpen: { text: "Days open" },
  phase: { text: "Phase" },
  post: { text: "Post" },
  postingStrategy: { text: "Posting strategy" },
  originalPostingType: { text: "Original posting type" },
  hiringRequest: { text: "Hiring Request" },
  reassignmentRequest: { text: "Reassignment request" 


},
  // F-185 (the reviewer, 26 Oct: one page grammar): the record's first tab and its overview cards' titles
  overview: { text: "Overview" },
  plan: { text: "Plan" },
  people: { text: "People" },
  status: { text: "Status" },
  compensationReview: { text: "Compensation review" 


},
});

/**
 * F-186 (the reviewer, 26 Oct): the record's V1 controls that move into the requisition header's Actions menu — record
 * after the fact, the openings change and its review, and the intake form's print. His glossary has no entry for them
 * yet, so they cannot be defineLabels entries (each needs a verbatim quote of his); they are V1's own words, unchanged,
 * kept in this one place so the menu and the V1 dialogs it opens say the same thing until his words arrive.
 */
export const REQ_V1_ACTIONS = Object.freeze({
  recordAfterFact: "Record what already happened",
  changeOpenings: "Change openings",
  reviewOpeningsChanges: "Review openings changes",
  printIntake: "Print the intake form",
} as const);

const REASSIGN = "| Reassign Recruiter | His Step 2 verb on his role";
const GO_BACK = "| Go back to Requisitions | The link at the top of the Assign/reassign Recruiter page";
const CONVERTED = "| Converted → R-#### / From Hiring Request RQ-#### |";

/** B13 (F-147, F-149; DC-232, DC-233, DC-234): the queue page's way back, the TA Manager's direct reassignment and the
 * requisition's link back to the Hiring Request it was converted from. The button and the queue's title are G6's
 * *Assign/reassign Recruiter*; the dialog's verb is the design system's *Reassign*. */
export const ASSIGN_LABELS = /* @__PURE__ */ defineLabels({
  reassignRecruiter: { text: "Reassign Recruiter" },
  goBackToRequisitions: { text: "Go back to Requisitions" },
  fromHiringRequest: { text: "From Hiring Request" },
});

/** The rows of BRD chapter 6, in his order (G §13): a row the seat may not see is absent, never masked. */
export const COMP_ROW_LABELS = /* @__PURE__ */ defineLabels({
  recommendation: { text: "Recommendation / recommended hiring range" 


},
  proposedBase: { text: "Proposed base / bonus / commission" },
  salaryGrade: { text: "Salary grade" },
  marketData: { text: "Market data / internal range / positioning" 


},
  internalEquity: { text: "Internal equity / working notes / approval evidence" 


},
});

/** His rule sentences the requisition screens draw. */
export const REQ_RULES = /* @__PURE__ */ defineLabels({
  postingNotBlocked: { text: "Posting is not blocked by Compensation." },
  coordinatorCreatesPosts: { text: "Coordinator creates and posts immediately after HRBP approval" 


},
  preservePostingStrategy: { text: "Preserve proposed vs current posting strategy and reason for change." },
  compensationAlone: { text: "Compensation alone finalizes official recommendation/range." 


},
  hmLimited: { text: "HM limited to recommendation/range, proposed base, bonus, commission, requests/status/final terms." 


},
});

const HOLD_STATE = "| On Hold | \"Additional states: On Hold, Cancellation Pending/Cancelled, Reopen Pending/Reopened.\" \"Hold records paused/continued activities and dates.\"";
const CANCEL_STATE = "| Cancellation Pending / Cancelled | same; \"Cancellation requires reason, HRBP approval, TA Manager notice, final notes/reporting category and all candidates dispositioned.\"";
const REOPEN_STATE = "| Reopen Pending / Reopened | same; \"reopen preserves previous closure history.\"";
const STATE_CHANGE = "| Hold/cancel/reopen request | \"HM or TA team may request hold/cancel/reopen; HRBP approves; TA Manager notified.\"";
const GATE_WORDS = "*Search Readiness* · *Missing prerequisites* · *All candidates dispositioned* · *Record outcome*";

/** Hold, cancel and reopen (lane B2 S10): the kind a request asks for, the cancellation gate and the notice line. The
 * dialog's title, the verbs and the reason fields are the design system's (DS rows 60, 77, 83). */
export const GOV_LABELS = /* @__PURE__ */ defineLabels({
  cancellation: { text: "Cancellation" },
  reopen: { text: "Reopen" },
  taManagerNotified: { text: "TA Manager notified" },
  allCandidatesDispositioned: { text: "All candidates dispositioned" },
});

/** His rule sentences for each kind. */
export const GOV_RULES = /* @__PURE__ */ defineLabels({
  holdRecords: { text: "Hold records paused/continued activities and dates." },
  cancellationRequires: { text: "Cancellation requires reason, HRBP approval, TA Manager notice, final notes/reporting category and all candidates dispositioned." 


},
  reopenPreserves: { text: "Reopen preserves previous closure history." },
});

const LEVEL = "| Level | \"Framework loads by level.\" Levels he names: \"Sr Director and below\", \"VP+\"; Email cards \"Manager & Below\", \"Director\", \"VP+\".";
const COMMUNITY = "\"Capture segment, tags, reason/date added, last contact, notes and prior requisition; do not auto-enroll in campaigns.\"";
const LIST_FILTERS =
  "| Filters | \"Support date, recruiter, coordinator, HRBP, HM, function, department, brand, job family, area of interest, level, location, strategy, new/replacement, status, phase, health, days open, time in stage, source, internal/external, offer status, standardized reasons, saved views, grouping, sorting, security-trimmed drill-down and exports.\" |";

/** The Requisitions list on Mission Control (D-11): its column and filter names, and the Cards view of his email's
 * requisition cards. Last contact is his word where V1 said *Last activity* (DS §3.4, RQ-2). */
export const REQ_LIST_LABELS = /* @__PURE__ */ defineLabels({
  level: { text: "Level" },
  cards: { text: "Cards" },
  lastContact: { text: "Last contact" },
  status: { text: "Status" },
  brand: { text: "Brand" },
  filters: { text: "Filters" },
});
